/**
 * Portfolios query hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { portfoliosApi } from '../api'
import type { PortfolioCreate, PortfolioUpdate } from '../types'

export const PORTFOLIOS_KEY = ['portfolios']

export function usePortfolios() {
  return useQuery({
    queryKey: PORTFOLIOS_KEY,
    queryFn: () => portfoliosApi.list(),
  })
}

export function usePortfolio(portfolioId: string) {
  return useQuery({
    queryKey: [...PORTFOLIOS_KEY, portfolioId],
    queryFn: () => portfoliosApi.get(portfolioId),
    enabled: !!portfolioId,
  })
}

export function usePortfolioHoldings(portfolioId: string) {
  return useQuery({
    queryKey: [...PORTFOLIOS_KEY, portfolioId, 'holdings'],
    queryFn: () => portfoliosApi.getHoldings(portfolioId),
    enabled: !!portfolioId,
  })
}

export function useCreatePortfolio() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PortfolioCreate) => portfoliosApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_KEY })
    },
  })
}

export function useUpdatePortfolio() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ portfolioId, data }: { portfolioId: string; data: PortfolioUpdate }) =>
      portfoliosApi.update(portfolioId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_KEY })
    },
  })
}

export function useDeletePortfolio() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (portfolioId: string) => portfoliosApi.delete(portfolioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_KEY })
    },
  })
}
